import { useMemo } from 'react'
import PdfReportQuestionAnswer from './PdfReportQuestionAnswer'
import styled from 'styled-components'

const SectionStats = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
  padding: 8px 12px;
  background: linear-gradient(135deg, rgba(103, 121, 227, 0.08) 0%, rgba(103, 121, 227, 0.04) 100%);
  border-radius: 6px;
  border: 1px solid rgba(103, 121, 227, 0.15);
  font-size: 12px;
  color: #475569;
  flex-wrap: wrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  
  @media print {
    padding: 6px 10px;
    font-size: 11px;
    box-shadow: none !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  @media screen {
    &:hover {
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }
  }
`

const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => props.$color || '#cbd5e1'};
    
    @media print {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`

export default function PdfReportSectionHandler({ section, showHeader = true, questionNumberOffset = 0 }) {
  // Calculate statistics for this section
  const stats = useMemo(() => {
    const totalQuestions = section?.questions?.length || 0
    const answeredQuestions = section?.questions?.filter(
      (q) => !!(q?.answer?.response || q?.answer?.additionalResponse)
    ).length || 0
    const unansweredQuestions = totalQuestions - answeredQuestions
    
    return {
      total: totalQuestions,
      answered: answeredQuestions,
      unanswered: unansweredQuestions,
      completionRate: totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0,
    }
  }, [section?.questions])

  return (
    <>
      {/* Section Title - only show if showHeader is true (when not wrapped in SectionCard) */}
      {showHeader && (
        <div style={{
          background: 'rgba(103, 121, 227, 0.12)',
          color: '#4338ca',
          fontWeight: 700,
          fontSize: '15px',
          padding: '10px 14px',
          margin: '-20px -20px 12px -20px',
          borderRadius: '12px 12px 0 0',
          borderBottom: '2px solid rgba(103, 121, 227, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{
            width: '3px',
            height: '18px',
            background: '#4338ca',
            borderRadius: '2px',
          }}></span>
          {section?.title || 'Screening Questions'}
        </div>
      )}
      
      {/* Section Statistics - Visual summary */}
      {stats.total > 0 && (
        <SectionStats style={{ marginTop: showHeader ? '6px' : '0' }}>
          <StatItem $color="#10b981">
            <strong>{stats.answered}</strong> Answered
          </StatItem>
          {stats.unanswered > 0 && (
            <StatItem $color="#cbd5e1">
              <strong>{stats.unanswered}</strong> Not Answered
            </StatItem>
          )}
          <StatItem $color="#6779e3">
            <strong>{stats.total}</strong> Total
          </StatItem>
          <StatItem $color="#f59e0b">
            <strong>{stats.completionRate}%</strong> Complete
          </StatItem>
        </SectionStats>
      )}
      
      {/* Questions List - Compact structured */}
      <div style={{ marginTop: stats.total > 0 ? '12px' : '0' }}>
        {section?.questions?.map((question, idx) => {
          // Calculate question number sequentially across ALL sections
          // questionNumberOffset is the count of questions in all previous sections
          const questionNumber = questionNumberOffset + idx + 1
          
          return (
            <PdfReportQuestionAnswer
              key={question?.id || `question-${idx}`}
              question={question}
              questionNumber={questionNumber}
            />
          )
        })}
      </div>
    </>
  )
}
